import React, { useEffect, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { UserAccount } from '../../../data/models/user-account.model';
import * as uiSlice from '../../../data/ui.slice';
import * as userSlice from '../../../data/user.slice';
import * as securitySlice from '../../../data/security.slice';
import { UserCategoryType } from '../../../data/models/user-category-type.model';
import SelectMenu from '../../forms/SelectMenu';
import TextInput from '../../forms/TextInput';
import UserRolesForm from './UserRolesForm';
import SelectProviderDialog from './SelectProviderDialog';
import { Provider } from '../../../data/models/provider.model';
import ConfirmationDialog from '../../ConfirmationDialog';
import { trimObjectValues } from '../../../utils/string.utils';

const PREFIX = 'UserDialog';

const classes = {
  content: `${PREFIX}-content`,
  title: `${PREFIX}-title`,
  form: `${PREFIX}-form`,
  provider: `${PREFIX}-provider`,
  providerName: `${PREFIX}-providerName`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.title}`]: {
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
  },
  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 350,
  },
  [`& .${classes.provider}`]: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  [`& .${classes.providerName}`]: {
    flexGrow: 1,
  },
}));

interface UserDialogProps {
  open: boolean;
  user?: UserAccount;
  userCategoryTypes: UserCategoryType[];
  close: (saved: boolean) => void;
}

export default function UserDialog({
  open,
  user,
  userCategoryTypes,
  close,
}: UserDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');

  const saving = useAppSelector(userSlice.selectSavingUser);
  const saved = useAppSelector(userSlice.selectSavedUser);
  const savedMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const roles = useAppSelector(securitySlice.selectUserRoles);
  const emailValidation = useAppSelector(userSlice.selectEmailValidation);

  const [providerDialogOpen, setProviderDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const [haveRolesChanged, setHaveRolesChanged] = useState(false);
  const [initialRoles, setInitialRoles] = useState<number[]>([]);
  const [nonDriverCategories, setNonDriverCategories] = useState<
    UserCategoryType[]
  >([]);

  const isTPUser =
    user?.userCategoryTypeID ===
    userCategoryTypes.find(
      (t) => t.userCategoryTypeBC === 'TRANSPORTATIONPROVIDER',
    )?.userCategoryTypeID;

  const isReadOnly = user?.identityAccess === 'AzureAD';

  function arraysAreEqual(arr1: number[], arr2: number[]): boolean {
    return isEqual(
      arr1.sort((a, b) => a - b),
      arr2.sort((a, b) => a - b),
    );
  }

  useEffect(() => {
    if (userCategoryTypes?.length) {
      setNonDriverCategories(
        userCategoryTypes.filter((t) => t.userCategoryTypeBC !== 'DRIVER'),
      );
    }
  }, [userCategoryTypes]);

  useEffect(() => {
    if (arraysAreEqual(initialRoles, selectedRoles)) {
      setHaveRolesChanged(false);
    } else {
      setHaveRolesChanged(true);
    }
  }, [selectedRoles, initialRoles]);

  const {
    control,
    formState,
    watch,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
  } = useForm();

  const { isDirty } = formState;
  const emailAddress = watch('emailAddress');
  const userCategoryType = watch('userCategoryType');
  const applicationTitle = watch('application');
  const providerID = watch('providerID');

  useEffect(() => {
    if (userCategoryType) {
      const id = categoryTypeBCToID(userCategoryType);
      const app =
        userCategoryType === 'SERVICEACCOUNT'
          ? applicationTitle || undefined
          : undefined;
      dispatch(
        securitySlice.fetchUserRolesAsync({ categoryId: id, appTitleId: app }),
      );
    } else {
      dispatch(securitySlice.setUserRoles([]));
    }
  }, [userCategoryType, applicationTitle]);

  useEffect(() => {
    if (user && roles) {
      const userRoles = [
        ...(user?.userAccountRoles || []),
        ...(user?.userCategoryServiceAccounts || []),
        ...(user?.userCategoryTransportationProviders || []),
      ];

      const filtered = roles.filter((r) => {
        const idx = userRoles.findIndex((ur) => ur.userRoleID === r.userRoleID);
        return idx >= 0;
      });
      const selected: number[] = filtered.map((r) => r.userRoleID!);
      setSelectedRoles(selected);
      setInitialRoles(selected);
      reset({
        userCategoryType: categoryTypeIDToBC(user?.userCategoryTypeID),
        firstName: user?.userFirstName || '',
        lastName: user?.userLastName || '',
        emailAddress: user?.emailAddress || '',
        provider:
          user?.transportationProvider?.transportationProviderName || '',
        providerID: user?.transportationProviderID || '',
      });
    }
  }, [user, roles]);

  useEffect(() => {
    if (!user) {
      reset({
        userCategoryType: '',
        firstName: '',
        lastName: '',
        emailAddress: '',
        application: '',
        provider: '',
        providerID: '',
      });
    }
  }, [user]);

  useEffect(() => {
    if (emailAddress) {
      dispatch(userSlice.validateUniqueEmailAsync(emailAddress.trim()));
    }
  }, [emailAddress]);

  useEffect(() => {
    if (user && user.emailAddress === emailAddress) {
      clearErrors('emailAddress');
    } else if (!emailValidation.unique) {
      setError('emailAddress', {
        type: 'custom',
        message: t('users.emailAddressIsNotUnique'),
      });
    } else if (!emailValidation.valid) {
      setError('emailAddress', {
        type: 'custom',
        message: t('users.emailAddressIsNotValid'),
      });
    } else {
      clearErrors('emailAddress');
    }
  }, [emailValidation, emailAddress, user]);

  useEffect(() => {
    if (saved && savedMessage) {
      closeDialog(true);
    }
  }, [saved, savedMessage]);

  const categoryTypeBCToID = (bc: string): number => {
    const uct = userCategoryTypes.find((t) => t.userCategoryTypeBC === bc);
    return uct?.userCategoryTypeID || 0;
  };

  const categoryTypeIDToBC = (id?: number): string => {
    const uct = userCategoryTypes.find((t) => t.userCategoryTypeID === id);
    return uct?.userCategoryTypeBC || '';
  };

  const getUser = (data: any): UserAccount => {
    const isProviderAccount =
      data.userCategoryType === 'TRANSPORTATIONPROVIDER' ||
      data.userCategoryType === 'DRIVER';
    return {
      userAccountID: user ? user.userAccountID : undefined,
      userCategoryTypeID: !user
        ? categoryTypeBCToID(data.userCategoryType)
        : undefined,
      userFirstName: data.firstName,
      userLastName: data.lastName,
      emailAddress: data.emailAddress,
      transportationProviderID: isProviderAccount ? data.providerID : undefined,
      userRoleIDs: selectedRoles,
    };
  };

  const saveUser = React.useCallback(
    (data: any) => {
      const newUser = getUser(data);
      if (!user) {
        trimObjectValues(newUser, [
          'userFirstName',
          'userLastName',
          'emailAddress',
        ]);
        dispatch(userSlice.createUserAsync(newUser));
      } else {
        dispatch(userSlice.updateUserAsync(newUser));
      }
    },
    [user, userCategoryTypes, selectedRoles],
  );

  const handleConfirmationDialogOpen = () => {
    setConfirmOpen(true);
  };

  const handleConfirmationDialogClose = () => {
    setConfirmOpen(false);
  };

  const isFormDirty = () => {
    if (isDirty) {
      handleConfirmationDialogOpen();
    } else {
      cancelDialog();
    }
  };

  const closeDialog = (saved: boolean) => {
    setSelectedRoles([]);
    dispatch(securitySlice.setUserRoles([]));
    if (saved) {
      reset({
        userCategoryType: '',
        firstName: '',
        lastName: '',
        emailAddress: '',
        application: '',
        provider: '',
        providerID: '',
      });
      clearErrors('emailAddress');
    }
    close(saved);
  };

  const cancelDialog = () => {
    handleConfirmationDialogClose();
    closeDialog(false);
  };

  const selectProvider = () => {
    setProviderDialogOpen(true);
  };

  const handleProviderDialogClose = (provider?: Provider) => {
    if (provider) {
      setValue('provider', provider?.transportationProviderName || '', {
        shouldValidate: true,
      });
      setValue('providerID', provider?.transportationProviderID || '', {
        shouldValidate: true,
      });
    }
    setProviderDialogOpen(false);
  };

  const roleSelectionUpdate = (id: number, checked: boolean) => {
    const selections = [...selectedRoles];
    if (checked) {
      selections.push(id);
    } else {
      const idx = selections.indexOf(id);
      if (idx >= 0) {
        selections.splice(idx, 1);
      }
    }
    setSelectedRoles(selections);
  };

  return (
    <RootDialog
      className={classes.content}
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={isFormDirty}>
      <DialogTitle className={classes.title}>
        {user ? t('settings.editUser') : t('settings.createUser')}
      </DialogTitle>
      <DialogContent>
        <Box className={classes.content}>
          <Box className={classes.form}>
            <SelectMenu
              label={t('users.userType')}
              name="userCategoryType"
              values={nonDriverCategories}
              valueAttr="userCategoryTypeBC"
              labelAttr="userCategoryTypeName"
              disabled={!!user || isReadOnly}
              required={true}
              control={control}
              rules={{ required: requiredFieldMessage }}
            />
            <TextInput
              name="firstName"
              label={t('users.firstName')}
              control={control}
              required={true}
              rules={{ required: requiredFieldMessage }}
              inputProps={{ maxLength: 50 }}
              disabled={isReadOnly}
            />
            <TextInput
              name="lastName"
              label={t('users.lastName')}
              control={control}
              required={true}
              rules={{ required: requiredFieldMessage }}
              inputProps={{ maxLength: 50 }}
              disabled={isReadOnly}
            />
            <TextInput
              name="emailAddress"
              label={t('users.email')}
              control={control}
              required={true}
              rules={{ required: requiredFieldMessage }}
              errors={formState.errors}
              disabled={isReadOnly}
            />
            {userCategoryType === 'TRANSPORTATIONPROVIDER' && (
              <Box className={classes.provider}>
                <TextInput
                  name="provider"
                  className={classes.providerName}
                  label={t('users.provider')}
                  disabled={true}
                  control={control}
                  required={true}
                  rules={{ required: requiredFieldMessage }}
                />
                <Button
                  onClick={selectProvider}
                  variant="contained"
                  disabled={isReadOnly || isTPUser}
                  color="primary">
                  ...
                </Button>
              </Box>
            )}
          </Box>
          <Box>
            <UserRolesForm
              userRoles={roles}
              selectedRoles={selectedRoles}
              loadingUserRoles={false}
              readOnly={isReadOnly}
              updateSelection={roleSelectionUpdate}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={isFormDirty} variant="contained" color="secondary">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit(saveUser)}
          variant="contained"
          color="primary"
          disabled={
            !formState.isValid ||
            selectedRoles.length < 1 ||
            saving ||
            (!isDirty && !haveRolesChanged)
          }>
          {user ? t('common.update') : t('common.create')}
        </Button>
      </DialogActions>
      <SelectProviderDialog
        providerID={providerID || undefined}
        open={providerDialogOpen}
        close={handleProviderDialogClose}
      />
      <ConfirmationDialog
        title={t('common.discardChanges')}
        message={t('common.possibleDataLoss')}
        open={confirmOpen}
        positiveAction={cancelDialog}
        negativeAction={handleConfirmationDialogClose}
      />
    </RootDialog>
  );
}
